import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { COMMON_IMPORTS } from '../../common-imports';
import { AvLanguageDropdownComponent } from '../av-language-dropdown/av-language-dropdown.component';
import { AvRailComponent } from '../av-rail/av-rail.component';
import { AvSpinComponent } from '../av-spin/av-spin.component';
import { AvThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { AvPendingDirective } from '../../Directives/av-pending.directive';
import { LogoutService } from '../../Services/logout.service';
import { TranslationService } from '../../Services/translation.service';
import { waitState } from '../../Services/wait-state.util';
import { ConsentFacade } from '../../../../Facades/consent.facade';
import { ConsentPermissionRow } from '../../../../Interfaces/consent-permission-row.interface';
import { CONSENT_LOCK_REMEDY_PATH } from '../../../../Consts/consent-lock.const';

@Component({
  selector: 'av-consent-lock-dialog',
  standalone: true,
  imports: [
    COMMON_IMPORTS,
    AvLanguageDropdownComponent,
    AvThemeToggleComponent,
    AvRailComponent,
    AvSpinComponent,
    AvPendingDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './consent-lock-dialog.component.html',
  styleUrl: './consent-lock-dialog.component.scss',
})
export class ConsentLockDialogComponent {
  private readonly _consentFacade = inject(ConsentFacade);
  private readonly _logoutService = inject(LogoutService);
  private readonly _translateService = inject(TranslateService);
  private readonly _translationService = inject(TranslationService);

  readonly redirecting = this._consentFacade.redirecting;
  readonly unlocking = this._consentFacade.unlocking;
  readonly loadFailed = this._consentFacade.loadFailed;
  readonly canManageSubscription = this._consentFacade.canManageSubscription;
  protected readonly subscriptionPath = CONSENT_LOCK_REMEDY_PATH;

  readonly busy = computed(() => this.redirecting() || this.unlocking());

  protected readonly listWait = waitState(this._consentFacade.isLoading);
  protected readonly grantWait = waitState(this.busy);

  readonly permissionRows = computed<ConsentPermissionRow[]>(() => {
    this._translationService.currentLanguage();

    return this._consentFacade.permissions().map((id) => this.buildPermissionRow(id));
  });

  readonly errorMessage = computed<string | null>(() => {
    this._translationService.currentLanguage();
    const key = this._consentFacade.errorKey();

    return key === null ? null : this._translateService.instant(key);
  });

  onGrantConsent(): void {
    if (this.busy()) {
      return;
    }

    this._consentFacade.requestRedirect().subscribe({
      next: (url) => this.redirectToMicrosoft(url),
      error: () => undefined,
    });
  }

  onRetryLoad(): void {
    this._consentFacade.reload();
  }

  onSignOut(): void {
    this._logoutService.confirmLogout();
  }

  private buildPermissionRow(id: string): ConsentPermissionRow {
    const key = id.replace(/\./g, '_');
    const name = this.resolveText(`app.consentLock.permissions.${key}.name`, id);
    const hint = this.resolveText(`app.consentLock.permissions.${key}.hint`, id);

    return { id, name, tooltip: `${id} - ${hint}` };
  }

  private resolveText(key: string, fallback: string): string {
    const resolved = this._translateService.instant(key);

    return typeof resolved === 'string' && resolved !== key ? resolved : fallback;
  }

  private redirectToMicrosoft(url: string): void {
    window.location.href = url;
  }
}
